// Dhaka wall-clock time in, UTC out (non-negotiable #8). Moved here from the S18 exam
// sessions API in S19, whose scheduler types dates and times too.
/** D5: the organisation runs on Asia/Dhaka, which keeps +06:00 all year (no daylight saving). */
const DHAKA_OFFSET = '+06:00';
const DHAKA_OFFSET_MS = 6 * 3_600_000;

/** A date and time typed in Dhaka, as the UTC instant the server stores (non-negotiable #8). */
export function dhakaToIso(date: string, time: string): string | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date) || !/^\d{2}:\d{2}$/.test(time)) return null;
  const instant = new Date(`${date}T${time}:00${DHAKA_OFFSET}`);
  return Number.isNaN(instant.getTime()) ? null : instant.toISOString();
}

/** The reverse, to fill the date and time controls. */
export function isoToDhaka(iso: string | null): { readonly date: string; readonly time: string } {
  if (!iso) return { date: '', time: '' };
  const shifted = new Date(new Date(iso).getTime() + DHAKA_OFFSET_MS).toISOString();
  return { date: shifted.slice(0, 10), time: shifted.slice(11, 16) };
}

/** A Dhaka calendar date (yyyy-mm-dd) that really exists, or null. */
function validDate(date: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) return false;
  const probe = new Date(`${date}T00:00:00Z`);
  return !Number.isNaN(probe.getTime()) && probe.toISOString().slice(0, 10) === date;
}

/** The first instant of a Dhaka day (midnight in Dhaka), as the UTC instant the server stores. */
export function dhakaDayStartIso(date: string): string | null {
  return validDate(date) ? new Date(`${date}T00:00:00${DHAKA_OFFSET}`).toISOString() : null;
}

/** The last second of a Dhaka day (23:59:59 in Dhaka): a window "until" a date runs to the end of that day, in Dhaka, not in UTC. */
export function dhakaDayEndIso(date: string): string | null {
  return validDate(date) ? new Date(`${date}T23:59:59${DHAKA_OFFSET}`).toISOString() : null;
}

/** Today's date in Dhaka (the organisation's day), not in UTC: between midnight and 06:00 in Dhaka the UTC date is still yesterday. */
export function dhakaToday(now: Date = new Date()): string {
  return isoToDhaka(now.toISOString()).date;
}

/** A date some days later (or earlier, with a negative number), as a Dhaka calendar date. */
export function addDhakaDays(date: string, days: number): string {
  const moved = new Date(`${date}T00:00:00Z`);
  moved.setUTCDate(moved.getUTCDate() + days);
  return moved.toISOString().slice(0, 10);
}
